export interface LoginRequest {
  user_name: string;
  password: string;
}

export interface RefreshTokenRequest {
  refreshtoken: string;
}

export interface LoginResponse {
  status: string;
  description?: string;
  accesstoken?: string;
  refreshtoken?: string;
}

export interface Feature {
  name: string;
  enabled?: boolean;
  quota?: number;
}

export interface LicenseDetails {
  account_id: string;
  email: string;
  registered_nodes_limit: string;
  activated_nodes_limit: string;
  version: string;
  creation_timestamp?: number;
  expiration_months?: number;
  expiration_timestamp?: number;
  payment_mode?: string;
  features?: Feature[];
}

export interface LicenseResponse {
  licenses: LicenseDetails[];
  total?: number;
  next_id?: string;
}

export interface LicenseUpdateRequest {
  account_id: string;
  email: string;
  registered_nodes_limit: string;
  activated_nodes_limit: string;
  version: string;
  creation_timestamp?: number;
  expiration_months?: number;
  expiration_timestamp?: number;
  payment_mode?: string;
  features?: Feature[];
}

export interface NewLicenseRequest {
  account_id: string;
  email: string;
  registered_nodes_limit: string;
  activated_nodes_limit: string;
  version?: string;
  expiration_months?: number;
  payment_mode?: string;
  features?: Feature[];
}

// ---- License environment (License module only) ----
export type LicenseEnv = 'PROD' | 'DEV';

// ---- v2 license model (SuperAdmin API) ----
export interface BillGenerationDate {
  date: number;
  month: number;
}

export interface LicenseBatch {
  batch_num?: number;
  nodes_limit: number;
  expiration_months?: number;
  device_type?: string;
  category?: number;
  status?: 'active' | 'inactive';
  matter_fabric_enabled?: boolean;
  matter_trial_quota?: number;
  camera_quota?: number;
  fwpn?: string;
}

export interface V2LicenseDetails {
  customer_name?: string;
  account_id: string;
  email: string;
  region: string;
  secondary_emails?: string[];
  version: string;
  payment_mode: string;
  registered_nodes_limit?: string;
  batches?: LicenseBatch[];
  bill_generation_date?: BillGenerationDate;
  grace_period?: number;
  // Response-only computed fields
  fwpns?: string[];
  matter_fabric_enabled?: boolean;
  total_matter_trial_quota?: number;
  total_camera_quota?: number;
  total_nodes_limit?: number;
  creation_timestamp?: number;
  expiration_timestamp?: number;
}

export interface NewV2LicenseRequest {
  customer_name?: string;
  account_id: string;
  email: string;
  region: string;
  secondary_emails?: string[];
  version: string; // 'v2'
  payment_mode: string; // 'LICENSE' | 'SUBSCRIPTION'
  registered_nodes_limit?: string;
  batches?: LicenseBatch[];
  bill_generation_date?: BillGenerationDate;
  grace_period?: number;
  send_email?: boolean;
}

export interface V2LicenseUpdateRequest {
  customer_name?: string;
  email?: string;
  secondary_emails?: string[];
  operation?: 'add' | 'remove';
  registered_nodes_limit?: string;
  region?: string;
  batches?: LicenseBatch[];
  bill_generation_date?: BillGenerationDate;
  grace_period?: number;
  // Not in the documented update schema; sent best-effort (backend may ignore on PUT).
  send_email?: boolean;
}

// A license record from the GET list can be either v1 or v2 shaped.
export type AnyLicense = LicenseDetails & Partial<V2LicenseDetails>;

export const isV2License = (license: { version?: string; batches?: unknown; customer_license?: unknown }): boolean =>
  license.version === 'v2' || Array.isArray(license.batches) || !!license.customer_license;

export interface QuotaRequest {
  user_name: string;
  quota: number;
  esp32_whitelist?: boolean;
}

export interface GenericResponse {
  status: string;
  description?: string;
}

export interface SarRequest {
  account_id: string;
  email: string;
  region: string;
  module?: string;
}

export interface SarAllAccessRequest {
  all_customers?: boolean;
}

export interface SarRevokeRequest {
  account_id: string;
  region: string;
}

export interface SarNotifyRequest {
  release_version: string;
  deployment_manual_url: string;
  release_notes_url: string;
  email_list: string[];
}